import * as ko from 'knockout'
import Rx from 'rx'
import { debounce } from '@mobi/utils/functions'
import type { ToteSelection, RaceDetails, Acceptor, Race, BetSource } from '@mobi/betslip/types'
import { removeToast } from '@mobi/component-library/Feedback/Toast'
import { Disposable } from '@classic/AppUtils/Framework/Disposable/Disposable'
import Guard from '@classic/AppUtils/Framework/Guard'
import * as EventAggregator from '@classic/AppUtils/Framework/Messaging/EventAggregator'
import * as FormGiddyUpRaceInformation from '@classic/Betting-v2/Components/Form/FormGiddyUpRaceInformation'
import { BetType } from '@classic/Betting-v2/Model/Betting/BetType'
import { BettingInformation } from '@classic/Betting-v2/Model/BettingInformation'
import { doBetEnquiry } from '@classic/Betting-v2/Model/LegacyBetAdapter'
import { IObservableStarter } from '@classic/Betting-v2/Model/Observables/IObservableStarter'
import ObservableRacePage from '@classic/Betting-v2/Model/Observables/ObservableRacePage'
import { BetSelectionBuilder } from '@classic/Betting-v2/Model/BetSelectionBuilder'
import SelectionProcessor from '@classic/Betting-v2/Components/Core/Processors/Processor'
import { CheckBoxSelection } from '@classic/Betting-v2/Components/Core/UIElements/CheckboxSelection'
import { ButtonSelectionType } from '@classic/Betting-v2/Components/Core/UIElements/ButtonSelectionType'
import * as buttons from '@classic/Betting-v2/Components/Core/UIElements/ButtonsSelection'
import { giddyUpViewed } from '@classic/Foundation/Analytics/Analytics'
import ObservableFixedOddsStarterInfo from '@classic/Betting-v2/Model/Observables/ObservableFixedOddsStarterInfo'
import { getRacePushDataTopics } from './helpers/getRacePushDataTopics'
import { BettingDrawer } from '@core/Components/BettingDrawer/BettingDrawer'
import { SameRaceMultiBettingDrawer } from '@core/Areas/Racing/Components/SameRaceMulti/SameRaceMultiBettingDrawer/SameRaceMultiBettingDrawer'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { IUseGiddyUp } from './IUseGiddyUp'
import { QuickbetClosed, RaceClosedEventReceived } from '@core/Areas/Quickbet/signals'
import {
  iotSubscribeTopics as subscribeToRaceEvents,
  iotUnsubscribeTopics as unsubscribeFromRaceEvents,
  EventData,
  FobPriceChangedPushEvent,
  TotePriceChangedPushEvent,
  ToteAcceptorScratchedPushEvent,
  ToteAcceptorUnscratchedPushEvent,
  ToteRaceStatusChangedPushEvent,
} from '@core/State/PushData'
import { CHANGE_BET_TYPE_EVENT } from '@core/Areas/RaceCard/constants'
import { setCurrentBetType, setSelectedProposition } from '@core/Areas/RaceCard/Store'
import { ClearRaceBettingPageMysterySelection } from '@core/Areas/Racing/Components/Mystery/MysteryRaceBettingPageWrapperDriver'
import { getTypeAndStartersFromRaceUri } from '@core/Areas/Racing/helpers'
import { store } from '@core/Store'
import {
  getSelectionPrice,
  SameRaceMultiPriceResponse,
} from '@core/Areas/Racing/Components/SameRaceMulti/SameRaceMultiBettingDrawer/SameRaceMultiPrice'
import RaceTime from '@core/Areas/Racing/Components/RaceTime'
import { SrmGridPriceChangedPushEvent } from 'tabtouch-push-contract'
import { StartersKeyValue } from '@classic/Betting-v2/Model/Observables/ObservableRaceStarters'
import {
  pushTotePriceChanged,
  pushFobPriceChanged,
  pushSameRaceMultiPriceChanged,
  pushToteAcceptorScratched,
  pushToteAcceptorUnscratched,
  pushToteRaceStatusChanged,
} from './helpers'
import LegSelectionContext from '../../Core/Processors/LegSelectionContext'
import { ISelectionMadeCommand } from '../../Core/Processors/ISelectionMadeContext'
import { AppRoutes } from '@core/AppRoutes'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import { showWarningMessage } from '@classic/Betting-v2/Utils/ErrorHandler'
import { GiddyUpModalCloseTopic } from './constants'
import { logError } from '@core/Utils'
import { formatFixtureDateToSimpleString } from '@core/Areas/Racing/helpers/formatFixtureDateToSimpleString'

export class StartersPageViewModel extends Disposable {
  private selectionProcessor!: SelectionProcessor
  private selectedRaceNumberChangedSubscription!: ko.Subscription
  private selectedBetTypeSubscription!: ko.Subscription
  public isValid!: ko.PureComputed<boolean>
  private isValidSubscriptionGiddyUp: ko.Subscription | undefined
  public raceInformation!: ObservableRacePage
  public bettingInformation!: BettingInformation
  public selectedRaceStarters!: ko.Observable<StartersKeyValue | undefined>
  public starterSelections!: ko.PureComputed<boolean[][]>
  public mergeDone!: ko.PureComputed<boolean>
  public isAllUp!: ko.PureComputed<boolean>
  public hasGiddyUpRaceKey!: ko.PureComputed<boolean>
  public raceStatus: ko.Observable<string> = ko.observable('')
  private quickbetCloseSubscription!: Rx.Disposable
  private isSelectingQuaddieStarters = false
  public BettingDrawer = BettingDrawer
  public SameRaceMultiBettingDrawer = SameRaceMultiBettingDrawer
  public PLACE_QUICKBET = 'QUICKBET'
  public PLACE_BETSLIP = 'BETSLIP'
  private raceEventSubscription!: Rx.IDisposable | null
  private currentSubscribedRaceNumber!: number | null
  private currentSubscribedTopics!: string[]
  private RACE_CLOSED_TOAST_ID: string | null = null
  public STARTER_ID_PREFIX = 'starter_'
  public useGiddyUp?: IUseGiddyUp
  public RaceTime = RaceTime

  constructor(params: {
    raceInformation: ObservableRacePage
    bettingInformation: BettingInformation
  }) {
    super()

    Guard.notNull(params)
    Guard.notNull(params.raceInformation)
    Guard.notNull(params.bettingInformation)

    this.bettingInformation = params.bettingInformation

    // Sync Initial Value w/ RaceCard Redux Store
    store.dispatch(setCurrentBetType(this.bettingInformation.selectedBetType().betType()))

    this.raceInformation = params.raceInformation
    this.raceStatus = this.raceInformation.meetingInformation.selectedRace.raceStatus
    this.bettingInformation.meetingInformation = this.raceInformation.meetingInformation

    this.selectedRaceStarters = ko.observable()
    this.updateSelectedRaceStarters()
    this.mergeDone = ko.pureComputed<boolean>(
      () => this.bettingInformation.mergeDone() && this.raceInformation.mergeDone()
    )

    this.selectionProcessor = new SelectionProcessor(this.bettingInformation, this.raceInformation)
    this.mergeDisposables(this.selectionProcessor.callBacksForDisposal())
    this.isValid = ko.pureComputed<boolean>(
      () => this.validateSelections() && this.raceStatus() !== 'Closed'
    )
    this.starterSelections = ko.pureComputed<boolean[][]>(() => this.getStarterSelections())

    this.configureDisposal()
    this.registerHandlers()

    this.isAllUp = ko.pureComputed<boolean>(() =>
      this.bettingInformation.selectedBetType().isAllUp()
    )

    this.hasGiddyUpRaceKey = ko.pureComputed<boolean>(() => !!this.raceInformation.giddyUpRaceKey())

    this.useGiddyUp = {
      openGiddyUp: this.openGiddyUp.bind(this),
      showGiddyUpInTopPanel: this.showGiddyUpInTopPanel.bind(this),
      showGiddyUpInInformationPanel: this.showGiddyUpInfoPanel.bind(this),
    }

    EventAggregator.subscribe('last-starter-initialised', this.quickbetOnHighlightedStarter)
  }

  private showGiddyUpInfoPanel = (raceNumber: number): boolean => {
    return this.showGiddyUpInTopPanel(raceNumber)
  }

  private showGiddyUpInTopPanel = (raceNumber: number) => {
    if (
      !this.raceInformation ||
      !this.raceInformation.meetingInformation ||
      !this.raceInformation.meetingInformation.races()
    )
      return false

    const foundRace = this.raceInformation.meetingInformation
      .races()
      .find(item => raceNumber === item.raceNumber())
    if (!foundRace) return false

    return foundRace.key() === this.raceInformation.giddyUpRaceKey()
  }

  private registerHandlers() {
    this.selectedRaceNumberChangedSubscription = this.raceInformation.mergeDone.subscribe(
      finished => {
        if (finished) {
          const updatedStarters = this.raceInformation.getStartersForRace(
            this.bettingInformation.raceNumber
          )
          this.bettingInformation.updateSelectionsForCurrentRace(updatedStarters())
          this.updateSelectedRaceStarters()
        }
      }
    )

    this.selectedBetTypeSubscription = this.bettingInformation.selectedBetType.subscribe(betType =>
      store.dispatch(setCurrentBetType(betType.betType()))
    )

    // On Quickbet Close - Refresh Starters if Bet Complete or Error
    this.quickbetCloseSubscription = QuickbetClosed.signal$
      .withLatestFrom(quickbetState$, (_: unknown, quickbetState) => ({ quickbetState }))
      .subscribe(({ quickbetState }) => {
        const {
          betPlaced: isBetComplete,
          isAddingToBetslip,
          shouldKeepRaceCardSelections,
        }: QuickbetState = quickbetState.toJS()

        if (!shouldKeepRaceCardSelections && (isBetComplete || isAddingToBetslip)) {
          this.clearAllSelections()
        }
      })
  }

  private configureDisposal() {
    this.registerDisposals(() => {
      this.handleRemovingRaceClosedToast()
      this.selectedRaceNumberChangedSubscription.dispose()
      this.selectedBetTypeSubscription.dispose()
      this.quickbetCloseSubscription.dispose()
      this.unsubscribeFromRaceEvent()
      this.subscribeToRaceEventDebounced.cancel()
    })
  }

  private updateSelectedRaceStarters() {
    const currentRaceStatus =
      this.raceInformation.meetingInformation
        .races()
        .find(x => x.key() === this.raceInformation.meetingInformation.selectedRace.key())
        ?.raceStatus() || null

    if (currentRaceStatus && currentRaceStatus !== 'Closed') {
      this.raceStatus(currentRaceStatus)
      this.handleRemovingRaceClosedToast()
    }

    const raceStartersLocal = this.raceInformation.raceStarters
      .starters()
      .find(
        raceStarter =>
          raceStarter.Key.raceNumber() ===
          this.raceInformation.meetingInformation.selectedRace.raceNumber()
      )
    if (raceStartersLocal) this.selectedRaceStarters(raceStartersLocal)

    if (this.bettingInformation.raceNumber !== this.currentSubscribedRaceNumber) {
      if (this.raceEventSubscription) {
        this.unsubscribeFromRaceEvent()
      }
      this.subscribeToRaceEventDebounced()
    }
  }

  public validateSelections(): boolean {
    // guard against race/meeting that is undergoing a merge
    if (!this.mergeDone()) {
      return false
    }

    return this.bettingInformation.selectedBetType().validator.isValid(this.bettingInformation)
  }

  public getStarterSelections(): boolean[][] {
    // don't attempt to retrieve starter selections if it's not valid, e.g. starters not fully initialised, insufficient starters, etc.
    if (!this.validateSelections()) return []

    return this.bettingInformation
      .selectedBetType()
      .processor.getStarterSelections(
        this.raceInformation.getStartersForRace(this.bettingInformation.raceNumber)()
      )
  }

  public openGiddyUp = () => {
    // subscribe isValid changes to automatically open quickbet if/when any valid selection
    // combinations are made (corresponds to when toast is normally displayed)
    this.subscribeIsValidGiddyUp()
    // unsubscribe isValid changes when giddyup modal is closed
    const modalClosed = () => {
      EventAggregator.unsubscribe(GiddyUpModalCloseTopic, modalClosed)
      this.unsubscribeIsValidGiddyUp()
    }
    EventAggregator.subscribe(GiddyUpModalCloseTopic, modalClosed)
    // Set Giddy Up in the data layer
    giddyUpViewed()
    // show giddyup modal
    FormGiddyUpRaceInformation.openGiddyUp(
      this.raceInformation.giddyUpRaceKey(),
      this.raceInformation.giddyUpFixtureKey(),
      starterNumber =>
        this.raceInformation.raceStarters.getStarter(
          this.bettingInformation.raceNumber,
          starterNumber
        ),
      () =>
        this.raceInformation.meetingInformation.getRaceForGiddyUp(
          this.bettingInformation.raceNumber
        ),
      (starterNumber: number, isFixed: boolean) =>
        this.selectBetType(BetType.WinPlace, () =>
          this.selectWinAndPlaceStarters(starterNumber, isFixed)
        ),
      () => EventAggregator.publish('clear-all-selections-command'),
      (raceNumbers, acceptorNumbers) => {
        this.selectBetType(BetType.Quaddie, () => {
          this.selectQuaddieStarters(raceNumbers, acceptorNumbers)
        })
      }
    )
  }

  private selectBetType(betType: BetType, callback: () => void): void {
    if (this.bettingInformation.selectedBetType().betType() != betType) {
      // change bet type and invoke the callback, e.g. make selections
      const betTypeChangedCallback = () => {
        EventAggregator.unsubscribe('process-bet-type-completed', betTypeChangedCallback)
        callback()
      }
      EventAggregator.subscribe('process-bet-type-completed', betTypeChangedCallback)
      this.bettingInformation.selectedBetType().betType(betType)
      EventAggregator.publish(CHANGE_BET_TYPE_EVENT, { betType })
    } else {
      EventAggregator.publish('clear-all-selections-command')
      callback()
    }
  }

  private selectSameRaceMultiStarters(
    params: Pick<ReturnType<typeof getTypeAndStartersFromRaceUri>, 'starters' | 'source'>
  ): void {
    if (!params || !params.starters) return

    const selection: string[] = []

    params.starters.forEach(starterParam => {
      const starter = this.raceInformation.raceStarters.getStarter(
        this.bettingInformation.raceNumber,
        starterParam.starterNumber
      )

      if (
        starter === null ||
        starter.isFixedScratchedOrSuspended() ||
        (!starter.fixedOddsStarterInfo.isScratched() &&
          starter.fixedOddsStarterInfo.isSuspended()) ||
        starter.fixedOddsStarterInfo.sameRaceMultiPrices.isSuspended() != false
      )
        return

      // adapted from makeLegSelection in SelectionViewModel
      const starterSelection = starter.selection() as buttons.ButtonsSelection
      const legNumber = starterParam.legNumber // 1 to 4
      if (legNumber === undefined) return

      // starterSelection uses 0-based indexing, so subtract 1
      const selectedValue = starterSelection.values()[legNumber - 1]

      if (!selectedValue) {
        return
      }

      selectedValue(
        selectedValue() == ButtonSelectionType.None
          ? ButtonSelectionType.Fob
          : ButtonSelectionType.None
      )

      selection.push(`${starterParam.starterNumber}-${starterParam.legNumber}`)

      EventAggregator.publish('selection-made-command', {
        raceNumber: this.bettingInformation.raceNumber,
        starter: starter,
        context: new LegSelectionContext(legNumber, ButtonSelectionType.Fob),
      } as ISelectionMadeCommand)
    })

    if (selection.length === params.starters.length) {
      // potential improvement; reuse call by SameRaceMultiPrice's useQuery, instead of separate one
      getSelectionPrice(
        formatFixtureDateToSimpleString(this.raceInformation.meetingInformation.meetingDate()),
        this.raceInformation.meetingInformation.meetingId(),
        this.bettingInformation.raceNumber,
        selection.map(item => `selection=${item}`).join('&')
      ).then(price => this.placeSelection(this.PLACE_QUICKBET, price, params.source))
    }
  }

  private selectWinAndPlaceStarters(
    starterNumber: number,
    isFixed: boolean,
    source?: BetSource
  ): void {
    // find acceptor
    const starter = this.raceInformation.raceStarters.getStarter(
      this.bettingInformation.raceNumber,
      starterNumber
    )

    if (starter === null) return

    // scroll to starter
    const elementId = `${this.STARTER_ID_PREFIX}${starterNumber}`
    const element = document.getElementById(elementId)
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    const selection = starter.selection() as buttons.ButtonsSelection

    // select acceptor
    selection.value(isFixed ? ButtonSelectionType.Fob : ButtonSelectionType.Tote)

    EventAggregator.publish('selection-made-command', {
      raceNumber: this.bettingInformation.raceNumber,
      starter: starter,
      context: new LegSelectionContext(0, selection.value()),
      source,
    } as ISelectionMadeCommand)

    // show quickbet - not required for enhanced betslip as this will trigger automatically after the publishing 'selection-made-command'
    this.placeSelection(this.PLACE_QUICKBET, undefined, source)
  }

  private selectQuaddieStarters(raceNumbers: number[], acceptorNumbers: number[][]) {
    // guard against multiple invocations
    if (this.isSelectingQuaddieStarters) return
    this.isSelectingQuaddieStarters = true

    let raceIndex = 0
    let scratchedAcceptorCount = 0

    const startersInitialisedCallback = () => {
      // select starters
      acceptorNumbers[raceIndex].forEach(acceptor => {
        const starter = this.raceInformation.raceStarters.getStarter(
          raceNumbers[raceIndex],
          acceptor
        )

        if ((starter as IObservableStarter).isScratched()) {
          scratchedAcceptorCount++
        } else {
          const selection = (starter as IObservableStarter).selection() as CheckBoxSelection
          selection.values()[0].checked(true)
        }
      })

      if (++raceIndex == raceNumbers.length) {
        // all selections have been made so we can bail out
        EventAggregator.unsubscribe('last-starter-initialised', startersInitialisedCallback)
        this.bettingInformation.legVisible(-1)

        if (scratchedAcceptorCount > 0) {
          // disable auto quickbet until after the user has acknowledged the scratched modal
          this.unsubscribeIsValidGiddyUp()

          showWarningMessage(
            `Acceptor${scratchedAcceptorCount > 1 ? 's' : ''} Scratched`,
            'Please check your selections before proceeding.',
            () => {
              // re-enable auto quickbet & explicitly invoke quickbet as we would miss it whilst waiting for the scratched modal to close
              this.subscribeIsValidGiddyUp()
              this.placeSelection(this.PLACE_QUICKBET)
            }
          )
        }
        // clear the guard now that all the quaddie selections have been made
        this.isSelectingQuaddieStarters = false
      } else {
        // process next quaddie leg
        this.bettingInformation.legVisible(this.getLegNumber(raceNumbers[raceIndex]))
      }
    }
    EventAggregator.subscribe('last-starter-initialised', startersInitialisedCallback)

    // select leg (race) - this will result in last-starter-initialised firing
    const targetLeg = this.getLegNumber(raceNumbers[raceIndex])
    if (this.bettingInformation.legVisible() === targetLeg) {
      // this view is already rendered/initialised by ko, therefore we can immediately trigger the selection callback
      EventAggregator.publish('last-starter-initialised')
    } else {
      this.bettingInformation.legVisible(targetLeg)
    }
  }

  public placeSelection(
    type: string,
    priceResponse?: SameRaceMultiPriceResponse,
    betSource?: BetSource,
    winPlaceStarterOverride?: number
  ): void {
    EventAggregator.publish('stop-all-race-replay')
    if (!this.validateSelections()) return

    if (!betSource) betSource = getRaceCardBetSource()

    const bettingInfo = this.bettingInformation

    bettingInfo.assignMeetingInfo(this.raceInformation.meetingInformation)

    if (bettingInfo.isFixed()) {
      const selectionDetails = this.getRaceSelectionDetails(
        bettingInfo.isFixed(),
        undefined,
        winPlaceStarterOverride
      )

      const selection = BetSelectionBuilder.build({
        bettingInformation: bettingInfo,
        numberOfCombinations: 1,
        allUpFormulas: undefined,
        priceResponse,
        winPlaceStarterOverride,
      })

      if (type === this.PLACE_BETSLIP) {
        handleBetSelection({
          betFlow: { location: 'BetSlip' },
          selections: [{ ...selection, selectionDetails, betSource }],
        })
        this.clearAllSelectionsAndRefresh(true)
      }
      if (type === this.PLACE_QUICKBET) {
        handleBetSelection({
          betFlow: { location: 'Quickbet' },
          selections: [{ ...selection, selectionDetails, betSource }],
        })
      }
    } else {
      // potential future optimisation would be to remove the bet enquiry when using enhancedBetslip since it's n/a for W&P bets
      doBetEnquiry(bettingInfo, result => {
        const selection = BetSelectionBuilder.build({
          bettingInformation: bettingInfo,
          numberOfCombinations: result.TotalCombinations,
          allUpFormulas: result.AllUpFormulas,
          winPlaceStarterOverride,
        })

        const toteSelection = selection.selection as ToteSelection

        // override the win & place selection if a starter is provided, e.g. when enhancedBetslip is active where multiple starter selections can be present
        if (winPlaceStarterOverride) {
          toteSelection.numberOfCombinations = 1
          toteSelection.selectionString = winPlaceStarterOverride.toString()
        }

        const selectionString = toteSelection.selectionString // OK if it's undefined because of a bad cast
        const selectionDetails = this.getRaceSelectionDetails(
          bettingInfo.isFixed(),
          selectionString,
          winPlaceStarterOverride
        )

        if (type === this.PLACE_BETSLIP) {
          handleBetSelection({
            betFlow: { location: 'BetSlip' },
            selections: [{ ...selection, selectionDetails, betSource }],
          })
          this.clearAllSelectionsAndRefresh(true)
        }
        if (type === this.PLACE_QUICKBET) {
          handleBetSelection({
            betFlow: { location: 'Quickbet' },
            selections: [{ ...selection, selectionDetails, betSource }],
          })
        }
      })
    }
  }

  private clearAllSelectionsAndRefresh(refreshPage: boolean) {
    // always clear selections with an optional refresh page which acts as an optimisation to avoid unnecessary backend api calls
    if (refreshPage) this.refreshSelectionPage(true)
    else this.clearAllSelections()
  }

  private refreshSelectionPage(clearSelections: boolean): void {
    let raceNumbersToRefresh = [this.raceInformation.meetingInformation.selectedRace.raceNumber()]
    if (this.bettingInformation != null) {
      if (this.bettingInformation.selectedBetType().isDouble()) {
        raceNumbersToRefresh = this.raceInformation.getDoubleRaceNumbers()
      }
      if (this.bettingInformation.selectedBetType().isQuaddie()) {
        raceNumbersToRefresh = this.raceInformation.getQuaddieRaceNumbers()
      }
      if (this.bettingInformation.selectedBetType().isAllUp()) {
        raceNumbersToRefresh = [] //don't refresh for allUp bettype for performance reason as we don't in other parts of page
      }
    }
    EventAggregator.publish('race-refresh-command', raceNumbersToRefresh, clearSelections)
  }

  private clearAllSelections() {
    store.dispatch(setSelectedProposition(null))
    ClearRaceBettingPageMysterySelection()
    EventAggregator.publish('clear-all-selections-command')
  }

  private subscribeIsValidGiddyUp() {
    this.unsubscribeIsValidGiddyUp()

    this.isValidSubscriptionGiddyUp = this.isValid.subscribe(isValid => {
      if (isValid) this.placeSelection(this.PLACE_QUICKBET)
    })
  }

  private unsubscribeIsValidGiddyUp(): void {
    if (this.isValidSubscriptionGiddyUp != undefined) {
      this.isValidSubscriptionGiddyUp.dispose()
      this.isValidSubscriptionGiddyUp = undefined
    }
  }

  private createAcceptor(
    raceKey: string,
    observableStarter: IObservableStarter,
    isFixed: boolean
  ): Acceptor {
    const serializedSilks = JSON.stringify(observableStarter.silkImages())
    const imageUrlWithSizes = JSON.parse(serializedSilks)
    return {
      key: `${raceKey}-${observableStarter.number()}`,
      name: observableStarter.name(),
      number: observableStarter.number(),
      type: observableStarter.type(),
      imageUrl: observableStarter.silkImages().large.url,
      imageUrlWithSizes,
      meetingName: this.raceInformation.meetingInformation.meetingName(),
      fixedOdds: {
        win: '',
        place: '',
        isSuspended: observableStarter.fixedOddsStarterInfo.isSuspended(),
      },
      toteOdds: { win: '', place: '' },
      isScratched: isFixed
        ? observableStarter.isFixedScratchedOrSuspended()
        : observableStarter.isScratched(),
      scratchType: isFixed ? null : observableStarter.scratchType(),
    } as Acceptor
  }

  private getRaceSelectionDetails(
    isFixed: boolean,
    selectionString = '',
    preferredStarter?: number
  ): RaceDetails {
    const raceDetails: RaceDetails = { races: [], acceptors: [] }

    if (this.bettingInformation.selectedBetType().isNoveltyBet()) {
      raceDetails.races = [
        {
          key: this.raceInformation.meetingInformation.selectedRace.key(),
          raceNumber: this.raceInformation.meetingInformation.selectedRace.raceNumber(),
          meetingName: this.raceInformation.meetingInformation.meetingName(),
          meetingCode: this.raceInformation.meetingInformation.meetingCode(),
          meetingId: this.raceInformation.meetingInformation.meetingId(),
          meetingDate: this.raceInformation.meetingInformation.meetingDate().toString(),
          distance: this.raceInformation.meetingInformation.selectedRace.distance(),
          raceTime: this.raceInformation.meetingInformation.selectedRace.raceTime().toString(),
          type: this.raceInformation.meetingInformation.selectedRace.raceType(),
          acceptorKeys: [],
        },
      ]

      // novelty bet types have multiple selections for the same leg,
      // so look at the selection string to figure out which ones we need
      const singleStarterKeys = selectionString
        .split('/')
        .flatMap(starter => (starter.includes('.') ? starter.split('.') : starter))
      const raceKey = this.raceInformation.meetingInformation.selectedRace.key()
      raceDetails.acceptors = this.raceInformation.raceStarters
        .starters()
        .flatMap(rs => rs.Value())
        .filter(rs => singleStarterKeys.includes(rs.number().toString()))
        .map(rs => this.createAcceptor(raceKey, rs, isFixed))
      raceDetails.races[0].acceptorKeys = raceDetails.acceptors.map(a => a.key)

      return raceDetails
    }

    this.bettingInformation.getLegsForProcessing().forEach(leg => {
      const thisRaceKey = this.getRaceKeyByRaceNumber(leg.raceKey().raceNumber()) || ''
      const race: Race = {
        key: thisRaceKey,
        raceNumber: leg.raceKey().raceNumber(),
        meetingName: this.raceInformation.meetingInformation.meetingName(),
        meetingCode: this.raceInformation.meetingInformation.meetingCode(),
        meetingId: this.raceInformation.meetingInformation.meetingId(),
        meetingDate: this.raceInformation.meetingInformation.meetingDate().toString(),
        distance: parseInt(leg.raceKey().distance()),
        raceTime: leg.raceKey().startTime().toString(),
        type: this.raceInformation.meetingInformation.selectedRace.raceType(),
        acceptorKeys: [],
      }

      // if novelty, race.acceptorKeys will not contain all keys in selection
      // - only one acceptor is needed for name display/icon type (i.e. Trots, Dogs etc.)
      // - use the supplied starter number if available, else default to the first selection
      // - n/a for W&P fixed because WinPlaceFobProcessor.process() only maintains the last single starter selection, i.e. no need to pick through the collection of starters
      let starterOffset = 0
      if (preferredStarter) {
        const index = leg.starters().findIndex(starter => starter.number() == preferredStarter)
        if (index != -1) starterOffset = index
      }

      const betType = this.bettingInformation.selectedBetType().betType()
      let acceptors: Acceptor[]

      if (
        [BetType.AllUp, BetType.Double, BetType.Quaddie, BetType.SameRaceMulti].includes(betType)
      ) {
        acceptors = leg
          .starters()
          .map(starter => this.createAcceptor(thisRaceKey, starter, isFixed))
      } else {
        acceptors = [this.createAcceptor(thisRaceKey, leg.starters()[starterOffset], isFixed)]
      }

      raceDetails.acceptors = raceDetails.acceptors.concat(acceptors)

      race.acceptorKeys = raceDetails.acceptors
        .filter(a => a.key.indexOf(race.key + '-') === 0)
        .map(a => a.key)

      raceDetails.races.push(race)
    })

    return raceDetails
  }

  private getRaceKey(): string | null {
    return this.raceInformation?.meetingInformation?.selectedRace?.key?.() || null
  }

  private getRaceKeyByRaceNumber(raceNumber: number): string | null {
    if (
      this.raceInformation &&
      this.raceInformation.meetingInformation &&
      this.raceInformation.meetingInformation.races
    ) {
      const race = this.raceInformation.meetingInformation
        .races()
        .find(x => x.raceNumber() === raceNumber)
      return race ? race.key() : null
    }
    return null
  }

  private getLegNumber(raceNumber: number) {
    const startersKeyValue = this.raceInformation.raceStarters
      .starters()
      .find(raceStarter => raceStarter.Key.raceNumber() === raceNumber)
    return startersKeyValue != undefined ? startersKeyValue.Key.leg() : 0
  }

  private subscribeToRaceEvent() {
    if (this.raceEventSubscription) {
      this.unsubscribeFromRaceEvent()
    }
    const hasSameRaceMulti = this.raceInformation
      .availableBetTypes()
      .some(bt => bt.betType() === BetType.SameRaceMulti)

    this.currentSubscribedTopics = getRacePushDataTopics({
      raceId: this.getRaceKey(),
      raceInformation: this.raceInformation,
      hasSameRaceMulti,
    })
    this.currentSubscribedRaceNumber = this.bettingInformation.raceNumber

    const pushEventSubscriptions = subscribeToRaceEvents(this.currentSubscribedTopics)
    this.raceEventSubscription = pushEventSubscriptions.subscribe(this.pushEventReceived.bind(this))
  }

  private subscribeToRaceEventDebounced = debounce(this.subscribeToRaceEvent, 3000)

  private unsubscribeFromRaceEvent() {
    if (this.raceEventSubscription) {
      this.raceEventSubscription.dispose()
      this.raceEventSubscription = null
      this.currentSubscribedRaceNumber = null

      if (this.currentSubscribedTopics) {
        unsubscribeFromRaceEvents(this.currentSubscribedTopics)
        this.currentSubscribedTopics.length = 0
      }

      ClearRaceBettingPageMysterySelection()
    }
  }

  public static resetFobFavourite(starters: IObservableStarter[]) {
    const lowestPriceStarters = starters.reduce(
      (accu: ObservableFixedOddsStarterInfo[], starter) => {
        const fobInfo = starter.fixedOddsStarterInfo

        if (!fobInfo) {
          return accu
        }

        if (fobInfo.isFavourite()) {
          fobInfo.isFavourite(false)
        }

        if (fobInfo.isScratched() || fobInfo.isSuspended()) {
          return accu
        }

        if (accu.length === 0) {
          return [fobInfo]
        }

        const currentLowestPrice = Number.parseFloat(accu[0].displayWinDividend())
        const currentPrice = Number.parseFloat(fobInfo.displayWinDividend())

        if (currentPrice < currentLowestPrice) {
          return [fobInfo]
        } else if (currentPrice === currentLowestPrice) {
          accu.push(fobInfo)
        }
        return accu
      },
      []
    )

    if (lowestPriceStarters.length === 1) {
      lowestPriceStarters[0].isFavourite(true)
    }
  }

  private pushToteRaceStatusChanged(event: ToteRaceStatusChangedPushEvent) {
    if (event.status !== 'closed') return

    if (this.raceInformation.meetingInformation.selectedRace.key() !== event.raceId.toString()) {
      const raceKeyFromPage = this.raceInformation.meetingInformation.selectedRace.key()
      const raceKeyFromEvent = event.raceId.toString()
      logError(
        `pushToteRaceStatusChanged: no update because race key does not match event (${raceKeyFromPage} vs ${raceKeyFromEvent})`,
        true
      )
      return
    }

    this.RACE_CLOSED_TOAST_ID = `race-closed-${this.currentSubscribedRaceNumber}`
    RaceClosedEventReceived({ event, toastId: this.RACE_CLOSED_TOAST_ID })

    const { meetingId, meetingDate, raceNumber } = this.bettingInformation

    pushToteRaceStatusChanged({
      event,
      meetingId,
      meetingDate,
      raceNumber,
    })

    this.selectBetType(BetType.WinPlace, () => {
      setTimeout(() => {
        this.raceInformation.meetingInformation.selectedRace.raceStatus('Closed')

        // Giddyup doesn't read value from selectedRace, update the race directly
        const race = this.raceInformation.meetingInformation
          .races()
          .find(x => x.key() === this.raceInformation.meetingInformation.selectedRace.key())
        if (race) race.raceStatus('Closed')
      }, 500)
    })
  }

  private pushEventReceived(event: EventData) {
    if (!event || !event.payload) {
      return
    }

    switch (event.payload.eventType) {
      case 'ToteRaceStatusChanged':
        this.pushToteRaceStatusChanged(event.payload as ToteRaceStatusChangedPushEvent)
        break
      case 'FobPriceChanged': {
        const { meetingId, meetingDate, raceNumber } = this.bettingInformation
        pushFobPriceChanged({
          event: event.payload as FobPriceChangedPushEvent,
          raceInformation: this.raceInformation,
          meetingId,
          meetingDate,
          raceNumber,
          resetFobFavourite: StartersPageViewModel.resetFobFavourite,
        })
        break
      }
      case 'TotePriceChanged': {
        const { meetingId, meetingDate, raceNumber } = this.bettingInformation
        pushTotePriceChanged({
          event: event.payload as TotePriceChangedPushEvent,
          meetingId,
          meetingDate,
          raceNumber,
          raceInformation: this.raceInformation,
        })
        break
      }
      case 'ToteAcceptorScratched': {
        const { meetingId, meetingDate, raceNumber } = this.bettingInformation
        pushToteAcceptorScratched(
          event.payload as ToteAcceptorScratchedPushEvent,
          meetingId,
          meetingDate,
          raceNumber,
          this.getRaceKey(),
          this.raceInformation
        )
        break
      }
      case 'ToteAcceptorUnscratched': {
        const { meetingId, meetingDate, raceNumber } = this.bettingInformation
        pushToteAcceptorUnscratched(
          event.payload as ToteAcceptorUnscratchedPushEvent,
          meetingId,
          meetingDate,
          raceNumber,
          this.getRaceKey(),
          this.raceInformation
        )
        break
      }
      case 'SrmGridPriceChanged':
        pushSameRaceMultiPriceChanged({
          event: event.payload as SrmGridPriceChangedPushEvent,
          raceInformation: this.raceInformation,
        })
        break
    }
  }

  private handleRemovingRaceClosedToast() {
    if (this.RACE_CLOSED_TOAST_ID) {
      removeToast({ id: this.RACE_CLOSED_TOAST_ID })
      this.RACE_CLOSED_TOAST_ID = null
    }
  }

  public quickbetOnHighlightedStarter = () => {
    EventAggregator.unsubscribe('last-starter-initialised', this.quickbetOnHighlightedStarter)

    const { betType, starters, source, isValidUri } = getTypeAndStartersFromRaceUri()
    if (!isValidUri || !starters) return

    this.selectBetType(betType, () => {
      setTimeout(() => {
        if (betType === BetType.SameRaceMulti) {
          this.selectSameRaceMultiStarters({ starters, source })
        } else if (starters.length === 1) {
          this.selectWinAndPlaceStarters(starters[0].starterNumber, true, source)
        }
      }, 0)
    })
  }
}

export function getRaceCardBetSource(): BetSource {
  return isOnSkyPage() ? 'sky-page' : 'race-card'
}

function isOnSkyPage() {
  const skyPageRoute = AppRoutes.RaceCardSky.replace('/', '')
  const hashRoute = window.location.pathname.replace('/', '')

  return hashRoute === skyPageRoute
}
